
import React, { useState } from 'react';
import { IconPenTool, IconLayout, IconGift, IconSparkles, IconQuant } from '../components/Icons';
import { generateProductPitch } from '../services/geminiService';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import toast from 'react-hot-toast';

interface ProductLeg {
    id: string;
    type: 'Bond' | 'Call' | 'Put';
    direction: 'Long' | 'Short';
    strike: number;
    ratio: number;
}

const StructuringView: React.FC = () => {
    const [productName, setProductName] = useState('Autocallable Note (Phoenix)');
    const [underlying, setUnderlying] = useState('SPX');
    const [legs, setLegs] = useState<ProductLeg[]>([
        { id: 'L1', type: 'Bond', direction: 'Long', strike: 0, ratio: 1 },
        { id: 'L2', type: 'Put', direction: 'Short', strike: 90, ratio: 1 }, // Sell downside put
    ]);
    const [pitch, setPitch] = useState('');
    const [isDrafting, setIsDrafting] = useState(false);

    // Calculate Payoff Diagram
    const payoffData = React.useMemo(() => {
        const data = [];
        const spotStart = 80;
        const spotEnd = 120;
        
        for(let spot = spotStart; spot <= spotEnd; spot+=1) {
            let payoff = 0;
            
            legs.forEach(leg => {
                const dir = leg.direction === 'Long' ? 1 : -1;
                if (leg.type === 'Bond') {
                    payoff += 100 * dir * leg.ratio; // Par value
                } else if (leg.type === 'Call') {
                    payoff += Math.max(0, spot - leg.strike) * dir * leg.ratio;
                } else if (leg.type === 'Put') {
                    payoff += Math.max(0, leg.strike - spot) * dir * leg.ratio;
                }
            });
            
            // Normalize relative to par (100)
            data.push({ spot, payoff });
        }
        return data;
    }, [legs]);

    const addLeg = () => {
        const newLeg: ProductLeg = { 
            id: `L${legs.length + 1}`, 
            type: 'Call', 
            direction: 'Long', 
            strike: 100, 
            ratio: 1 
        };
        setLegs([...legs, newLeg]);
    };

    const updateLeg = (id: string, field: keyof ProductLeg, value: any) => {
        setLegs(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
    };

    const removeLeg = (id: string) => {
        setLegs(prev => prev.filter(l => l.id !== id));
    };

    const handleGeneratePitch = async () => {
        setIsDrafting(true);
        setPitch('');
        
        const structure = {
            name: productName,
            underlying,
            legs: legs.map(l => `${l.direction} ${l.ratio}x ${l.type} @ ${l.strike}`)
        };
        
        const text = await generateProductPitch(structure);
        setPitch(text);
        setIsDrafting(false);
    };

    return (
        <div className="flex h-full bg-slate-100">
            {/* Left: Builder */}
            <div className="w-96 bg-white border-r border-slate-200 flex flex-col p-6 overflow-y-auto custom-scrollbar">
                <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2 mb-1">
                    <IconPenTool /> Structuring Desk
                </h2>
                <p className="text-xs text-slate-500 mb-6">Visual Product Builder</p>

                <div className="space-y-6">
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Product Name</label>
                        <input 
                            type="text" 
                            value={productName}
                            onChange={(e) => setProductName(e.target.value)}
                            className="w-full border p-2 rounded text-sm font-bold text-slate-700"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Underlying</label>
                        <input 
                            type="text" 
                            value={underlying}
                            onChange={(e) => setUnderlying(e.target.value)}
                            className="w-full border p-2 rounded text-sm font-mono"
                        />
                    </div>

                    <div className="bg-slate-50 p-4 rounded border border-slate-200">
                        <h3 className="text-xs font-bold text-slate-700 uppercase mb-3 flex justify-between items-center">
                            Structure Legs
                            <button onClick={addLeg} className="text-blue-600 hover:text-blue-800 text-[10px] font-bold">+ ADD LEG</button>
                        </h3>
                        <div className="space-y-3">
                            {legs.map((leg, idx) => (
                                <div key={leg.id} className="bg-white p-3 rounded border border-slate-200 shadow-sm">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-bold text-slate-400">Leg {idx+1}</span>
                                        <button onClick={() => removeLeg(leg.id)} className="text-red-400 hover:text-red-600 text-xs">×</button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 mb-2">
                                        <select 
                                            value={leg.direction} 
                                            onChange={(e) => updateLeg(leg.id, 'direction', e.target.value)}
                                            className={`text-xs font-bold rounded p-1 border ${leg.direction === 'Long' ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : 'text-red-600 bg-red-50 border-red-200'}`}
                                        >
                                            <option>Long</option>
                                            <option>Short</option>
                                        </select>
                                        <select 
                                            value={leg.type} 
                                            onChange={(e) => updateLeg(leg.id, 'type', e.target.value)}
                                            className="text-xs font-bold rounded p-1 border border-slate-200 bg-white"
                                        >
                                            <option>Bond</option>
                                            <option>Call</option>
                                            <option>Put</option>
                                        </select>
                                    </div>
                                    {leg.type !== 'Bond' && (
                                        <div className="flex gap-2 items-center">
                                            <span className="text-xs text-slate-500">Strike:</span>
                                            <input 
                                                type="number" 
                                                value={leg.strike} 
                                                onChange={(e) => updateLeg(leg.id, 'strike', Number(e.target.value))}
                                                className="w-16 text-xs border rounded p-1"
                                            />
                                            <span className="text-xs text-slate-500">%</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <button 
                        onClick={() => toast.success("Structure Saved to Library")}
                        className="w-full py-3 bg-slate-800 text-white font-bold rounded shadow hover:bg-slate-900 flex items-center justify-center gap-2"
                    >
                        <IconLayout /> Save Structure
                    </button>
                </div>
            </div>

            {/* Right: Visualization & Sales */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 p-8 overflow-y-auto custom-scrollbar space-y-6">
                    {/* Payoff Chart */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                        <h3 className="font-bold text-slate-800 mb-4">Payoff Profile at Maturity</h3>
                        <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={payoffData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                    <XAxis dataKey="spot" label={{ value: 'Spot %', position: 'insideBottom', offset: -5 }} fontSize={12} domain={['dataMin', 'dataMax']} />
                                    <YAxis label={{ value: 'Payoff', angle: -90, position: 'insideLeft' }} fontSize={12} />
                                    <Tooltip contentStyle={{ borderRadius: '8px' }} />
                                    <ReferenceLine y={100} stroke="red" strokeDasharray="3 3" label="Par" />
                                    <Line type="monotone" dataKey="payoff" stroke="#3b82f6" strokeWidth={3} dot={false} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* AI Sales Pitch */}
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 flex flex-col">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="font-bold text-slate-800 flex items-center gap-2">
                                <IconGift /> Sales Pitch Generator
                            </h3>
                            <button 
                                onClick={handleGeneratePitch}
                                disabled={isDrafting}
                                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded font-bold text-xs flex items-center gap-2 transition-colors"
                            >
                                {isDrafting ? <span className="animate-spin">⏳</span> : <IconSparkles />}
                                Draft Email
                            </button>
                        </div>
                        
                        <div className="bg-slate-50 rounded-lg p-6 border border-slate-200 min-h-[200px] relative">
                            {pitch ? (
                                <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-line font-serif leading-relaxed">
                                    {pitch}
                                </div>
                            ) : (
                                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 italic">
                                    <IconQuant />
                                    <p className="mt-2">Click 'Draft Email' to generate a client-ready pitch using AiQ.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StructuringView;
